import * as PrismaTypes from '@prisma/client';
import * as AppTypes from './app-types';
import { SearchParams } from './search-params';
import { EncryptedVkData } from './session-types';
import * as UserTypes from './user-types';
import { OpenGraph } from 'next/dist/lib/metadata/types/opengraph-types';
import { SubscriptionForSchema } from './metadata-types';
import { Offer } from 'schema-dts';
import { BranchForSubscription, BranchWithParsedCases } from './branch-types';

export const isString = (text: unknown): text is string => {
  return typeof text === 'string' || text instanceof String;
};

export const isNumber = (text: unknown): text is number => {
  return typeof text === 'number' || text instanceof Number;
};

export const isBoolean = (value: unknown): value is boolean => {
  return typeof value === 'boolean' || value instanceof Boolean;
};

export function isDifficulty(value: unknown): value is PrismaTypes.Difficulty {
  return Object.values(PrismaTypes.Difficulty).includes(
    value as PrismaTypes.Difficulty
  );
}

export function isPlacement(value: unknown): value is PrismaTypes.Placement {
  return Object.values(PrismaTypes.Placement).includes(
    value as PrismaTypes.Placement
  );
}

export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

export function isISOString(value: unknown): value is string {
  if (!isString(value)) return false;
  const date = new Date(value);
  return isValidDate(date);
}

export function isISOStringOrNull(value: unknown): value is string {
  if (value === null) return true;

  if (!isString(value)) return false;
  const date = new Date(value);

  return isValidDate(date);
}

export function isNullableDate(value: unknown): value is Date | null {
  return value === null || (value instanceof Date && !isNaN(value.getTime()));
}

export function isAccessLevel(
  value: unknown
): value is PrismaTypes.AccessLevel {
  return Object.values(PrismaTypes.AccessLevel).includes(
    value as PrismaTypes.AccessLevel
  );
}

export function isSubscriptionDetails(
  obj: unknown
): obj is AppTypes.SubscriptionDetails {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'branchId' in obj &&
    isNumber(obj.branchId) &&
    'branchSlug' in obj &&
    isString(obj.branchSlug) &&
    'accessLevel' in obj &&
    isAccessLevel(obj.accessLevel) &&
    'expiresAt' in obj &&
    isISOString(obj.expiresAt)
  );
}

export function isSubscriptionDetailsArray(
  obj: unknown
): obj is AppTypes.SubscriptionDetails[] {
  return Array.isArray(obj) && obj.every(item => isSubscriptionDetails(item));
}

export function isSubscription(obj: unknown): obj is AppTypes.Subscription {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'planId' in obj &&
    isNumber(obj.planId) &&
    'name' in obj &&
    isString(obj.name) &&
    'allIn' in obj &&
    isBoolean(obj.allIn) &&
    'subscriptionDetails' in obj &&
    isSubscriptionDetailsArray(obj.subscriptionDetails)
  );
}

export function isThemeMode(value: unknown): value is AppTypes.ThemeMode {
  return value === 'light' || value === 'dark';
}

export function isUserPersonalData(
  obj: unknown
): obj is AppTypes.UserPersonalData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const necessaryFields = 'name' in obj && isString(obj.name);

  return (
    necessaryFields &&
    // Также проверяем, что если поле avatar существует, то оно либо undefined, либо строка
    ('avatar' in obj ? obj.avatar === undefined || isString(obj.avatar) : true)
  );
}

export function isUserPreferences(
  obj: unknown
): obj is AppTypes.UserPreferences {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'showAvatar' in obj &&
    isBoolean(obj.showAvatar) &&
    'theme' in obj &&
    isThemeMode(obj.theme)
  );
}

export function isUserClient(obj: unknown): obj is AppTypes.UserClient {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'personalData' in obj &&
    isUserPersonalData(obj.personalData) &&
    'preferences' in obj &&
    isUserPreferences(obj.preferences) &&
    'status' in obj &&
    isSubscriptionStatus(obj.status) &&
    'access' in obj &&
    isSubscriptionDetailsArray(obj.access)
  );
}

export function isSearchParams(obj: unknown): obj is SearchParams {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const { query, levels, page } = obj as SearchParams;

  return (
    (query === undefined || typeof query === 'string') &&
    (levels === undefined ||
      (Array.isArray(levels) && levels.every(l => typeof l === 'string'))) &&
    (page === undefined || typeof page === 'string')
  );
}

export function isEncryptedVkData(obj: unknown): obj is EncryptedVkData {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  return (
    'encryptedVKData' in obj &&
    isString(obj.encryptedVKData) &&
    'hmac' in obj &&
    isString(obj.hmac)
  );
}

export function isVKTokens(obj: unknown): obj is AppTypes.VKTokens {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'accessToken' in obj &&
    isString(obj.accessToken) &&
    'refreshToken' in obj &&
    isString(obj.refreshToken)
  );
}

export function isVKData(obj: unknown): obj is AppTypes.VKData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const vkTokens = isVKTokens(obj);

  const necessaryFields =
    vkTokens &&
    'refreshTokenExpiresAt' in obj &&
    isNumber(obj.refreshTokenExpiresAt) &&
    'deviceId' in obj &&
    isString(obj.deviceId);

  return (
    necessaryFields &&
    ('avatar' in obj ? obj.avatar === undefined || isString(obj.avatar) : true)
  );
}

export function isRegistrationData(
  obj: unknown
): obj is AppTypes.RegistrationData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'name' in obj &&
    isString(obj.name) &&
    'vkData' in obj &&
    isVKData(obj.vkData)
  );
}

export function isSignUpSessionForClient(
  obj: unknown
): obj is AppTypes.SignUpSessionForClient {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'registrationData' in obj &&
    isRegistrationData(obj.registrationData) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
}

export function isSignUpSessionForRedisIncomingData(
  obj: unknown
): obj is AppTypes.SignUpSessionForRedisIncomingData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'personalData' in obj &&
    isUserPersonalData(obj.personalData) &&
    'vkData' in obj &&
    isVKData(obj.vkData) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
}

export function isSignUpSessionForRedis(
  obj: unknown
): obj is AppTypes.SignUpSessionForRedis {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'encryptedRegistrationData' in obj &&
    isString(obj.encryptedRegistrationData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
}

export function isSubscriptionStatus(
  value: unknown
): value is PrismaTypes.SubscriptionStatus {
  return Object.values(PrismaTypes.SubscriptionStatus).includes(
    value as PrismaTypes.SubscriptionStatus
  );
}

export function isUserDataForSessionsDB(
  obj: unknown
): obj is AppTypes.UserDataForSessionsDB {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const encryptedData =
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj;

  const openData =
    'preferences' in obj &&
    isUserPreferences(obj.preferences) &&
    'subscriptionStatus' in obj &&
    isSubscriptionStatus(obj.subscriptionStatus) &&
    'planId' in obj &&
    isNumber(obj.planId);

  return 'id' in obj && encryptedData && openData;
}

export function isSessionRedis(obj: unknown): obj is AppTypes.SessionRedis {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'pgSessionId' in obj &&
    isString(obj.pgSessionId) &&
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    'subscriptionStatus' in obj &&
    isSubscriptionStatus(obj.subscriptionStatus) &&
    'subscriptionExpiresAt' in obj &&
    isISOStringOrNull(obj.subscriptionExpiresAt) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
}
export function isSessionClient(obj: unknown): obj is AppTypes.SessionClient {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const sessionClientFromUserServiceFields =
    isSessionClientFromUserService(obj);

  return (
    sessionClientFromUserServiceFields &&
    'pgSessionId' in obj &&
    isString(obj.pgSessionId)
  );
}

export function isSessionClientFromUserService(
  obj: unknown
): obj is AppTypes.SessionClientFromUserService {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'userId' in obj &&
    isString(obj.userId) &&
    'personalData' in obj &&
    isUserPersonalData(obj.personalData) &&
    'preferences' in obj &&
    isUserPreferences(obj.preferences) &&
    'subscriptionStatus' in obj &&
    isSubscriptionStatus(obj.subscriptionStatus) &&
    'subscriptionExpiresAt' in obj &&
    isISOStringOrNull(obj.subscriptionExpiresAt) &&
    'subscription' in obj &&
    isSubscription(obj.subscription)
  );
}

export function isSessionIdAndUserClient(
  obj: unknown
): obj is AppTypes.SessionIdAndUserClient {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'sessionId' in obj &&
    isString(obj.sessionId) &&
    'user' in obj &&
    isUserClient(obj.user)
  );
}

export function isConfigUrlData(obj: unknown): obj is AppTypes.ConfigUrlData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'id' in obj &&
    isNumber(obj.id) &&
    'difficulty' in obj &&
    isDifficulty(obj.difficulty) &&
    'topicSlug' in obj &&
    isString(obj.topicSlug)
  );
}

export function isPaginationData(obj: unknown): obj is AppTypes.PaginationData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'total' in obj &&
    isNumber(obj.total) &&
    'topicId' in obj &&
    isNumber(obj.topicId) &&
    'searchParams' in obj &&
    isSearchParams(obj.searchParams)
  );
}

export function isCreateUserArgs(
  obj: unknown
): obj is UserTypes.CreateUserArgs {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'hashedVkId' in obj &&
    isString(obj.hashedVkId) &&
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    'preferences' in obj &&
    isUserPreferences(obj.preferences)
  );
}

export function isUpdatePersonalDataArgs(
  obj: unknown
): obj is UserTypes.UpdatePersonalDataArgs {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'encryptedPersonalData' in obj &&
    isString(obj.encryptedPersonalData) &&
    'hmac' in obj &&
    isString(obj.hmac) &&
    ('preferences' in obj
      ? obj.preferences === undefined || isUserPreferences(obj.preferences)
      : true)
  );
}

export function isBranchForSubscription(
  obj: unknown
): obj is BranchForSubscription {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'id' in obj &&
    isNumber(obj.id) &&
    'name' in obj &&
    isString(obj.name) &&
    'slug' in obj &&
    isString(obj.slug) &&
    'price' in obj &&
    isNumber(obj.price)
  );
}

export function isBranchForSubscriptionArray(
  obj: unknown
): obj is BranchForSubscription[] {
  return Array.isArray(obj) && obj.every(item => isBranchForSubscription(item));
}

export function isAllInSubscriptionTableData(
  obj: unknown
): obj is AppTypes.AllInSubscriptionTableData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'key' in obj &&
    isNumber(obj.key) &&
    'branchName' in obj &&
    isString(obj.branchName)
  );
}

export function isCustomSubscriptionTableData(
  obj: unknown
): obj is AppTypes.CustomSubscriptionTableData {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  if (!isAllInSubscriptionTableData) return false;
  return (
    'price' in obj &&
    isNumber(obj.price) &&
    'label' in obj &&
    isString(obj.label)
  );
}

export function isOpenGraphImageDB(
  obj: unknown
): obj is AppTypes.OpenGraphImageDB {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'url' in obj &&
    isString(obj.url) &&
    'width' in obj &&
    isString(obj.width) &&
    'height' in obj &&
    isString(obj.height) &&
    'alt' in obj &&
    isString(obj.alt)
  );
}

export function isOpenGraphImageDBArray(
  obj: unknown
): obj is AppTypes.OpenGraphImageDB[] {
  return Array.isArray(obj) && obj.every(item => isOpenGraphImageDB(item));
}

export function isOpenGraphDB(obj: unknown): obj is AppTypes.OpenGraphDB {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return 'images' in obj && isOpenGraphImageDBArray(obj.images);
}

export function isOffer(obj: unknown): obj is Offer {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  const offer = obj as {
    '@type'?: unknown;
    name?: unknown;
    priceCurrency?: unknown;
    price?: unknown;
    availability?: unknown;
    url?: unknown;
  };
  return (
    offer['@type'] === 'Offer' &&
    'name ' in offer &&
    isString(offer.name) &&
    'priceCurrency' in offer &&
    isString(offer.priceCurrency) &&
    'price' in offer &&
    isString(offer.price) &&
    'availability' in offer &&
    isString(offer.availability) &&
    'url' in offer &&
    isString(offer.url)
  );
}

export function isOffersArray(obj: unknown): obj is Offer[] {
  return Array.isArray(obj) && obj.every(item => isOffer(item));
}

export function isMetadataClient(obj: unknown): obj is AppTypes.MetadataClient {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'pageSlug' in obj &&
    isString(obj.pageSlug) &&
    'title' in obj &&
    isString(obj.title) &&
    'description' in obj &&
    isString(obj.description) &&
    'openGraph' in obj &&
    isOpenGraph(obj.openGraph) &&
    (!('offer' in obj) || // offer может отсутствовать
      isOffersArray(obj.offer)) // или должен быть массивом
  );
}

export function isOpenGraphImage(obj: unknown): obj is AppTypes.OpenGraphImage {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return 'url' in obj && isString(obj.url);
}

export function isOpenGraphImagesArray(
  obj: unknown
): obj is AppTypes.OpenGraphImage[] {
  return Array.isArray(obj) && obj.every(item => isOpenGraphImage(item));
}

export function isOpenGraph(obj: unknown): obj is OpenGraph {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'type' in obj &&
    isString(obj.type) &&
    'url' in obj &&
    isString(obj.url) &&
    'title' in obj &&
    isString(obj.title) &&
    'description' in obj &&
    isString(obj.description) &&
    'siteName' in obj &&
    isString(obj.siteName) &&
    'images' in obj &&
    isOpenGraphImagesArray(obj.images)
  );
}

export function isSubscriptionForSchema(
  obj: unknown
): obj is SubscriptionForSchema {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'name' in obj &&
    isString(obj.name) &&
    'price' in obj &&
    isNumber(obj.price) &&
    'minPrice' in obj &&
    isNumber(obj.price) &&
    'maxPrice' in obj &&
    isNumber(obj.price) &&
    'active' in obj &&
    isBoolean(obj.active)
  );
}

export function isSubscriptionForSchemaArray(
  obj: unknown
): obj is SubscriptionForSchema[] {
  return Array.isArray(obj) && obj.every(item => isSubscriptionForSchema(item));
}

export function isSelectedFieldsForNavigaton(
  obj: unknown
): obj is AppTypes.SelectedFieldsForNavigaton {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'name' in obj && isString(obj.name) && 'slug' in obj && isString(obj.slug)
  );
}

export function isSelectedFieldsForNavigatonArray(
  obj: unknown
): obj is AppTypes.SelectedFieldsForNavigaton[] {
  return (
    Array.isArray(obj) && obj.every(item => isSelectedFieldsForNavigaton(item))
  );
}

export function isCases(obj: unknown): obj is AppTypes.Cases {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  return (
    'nominative' in obj &&
    isString(obj.nominative) &&
    'genitive' in obj &&
    isString(obj.genitive) &&
    'dative' in obj &&
    isString(obj.dative) &&
    'accusative' in obj &&
    isString(obj.accusative) &&
    'instrumental' in obj &&
    isString(obj.instrumental) &&
    'prepositional' in obj &&
    isString(obj.prepositional)
  );
}

export const isBranchWithParsedCases = (
  obj: unknown
): obj is BranchWithParsedCases => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  return (
    'slug' in obj &&
    isString(obj.slug) &&
    'name' in obj &&
    isString(obj.name) &&
    'cases' in obj &&
    isCases(obj.cases) &&
    'topics' in obj &&
    isSelectedFieldsForNavigatonArray(obj.topics)
  );
};

export function isBranchWithParsedCasesArray(
  obj: unknown
): obj is BranchWithParsedCases[] {
  return Array.isArray(obj) && obj.every(item => isBranchWithParsedCases(item));
}
